import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type FullQueueRow } from '../lib/api';
import { RiskPill, StatusBadge } from '../components/atoms';
import { formatRelative } from '../lib/format';

type Tab = 'open' | 'waiting' | 'past_sla' | 'recent';

const TAB_LABEL: Record<Tab, string> = {
  open: 'Open',
  waiting: 'Waiting on Requester',
  past_sla: 'Past SLA',
  recent: 'Recently Reviewed',
};

const EMPTY_HEADLINE: Record<Tab, string> = {
  open: 'Inbox zero — nothing in your queue.',
  waiting: 'No requests waiting on a response.',
  past_sla: 'Nothing past SLA — nice work.',
  recent: 'No recent reviews yet.',
};

const EMPTY_SUB: Record<Tab, string> = {
  open: 'When employees submit a new request, it lands here automatically.',
  waiting: 'Once a requester replies, the request moves back to your Open tab.',
  past_sla: 'Items here will show up automatically as your Open requests age past the configured threshold.',
  recent: '',
};

export function ReviewQueuePage({ role }: Readonly<{ role: 'it' | 'legal' }>) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('open');
  const [search, setSearch] = useState('');
  const bundle = useQuery({
    queryKey: ['queue-bundle', role],
    queryFn: () => api.queueBundle(role),
    refetchInterval: 30_000,
  });

  const stats = bundle.data?.stats;
  const slaHours = stats?.slaHours ?? (role === 'it' ? 72 : 120);
  const term = stats?.slaTermLabel ?? 'SLA';

  const slaMs = slaHours * 60 * 60 * 1000;
  function listForTab(): FullQueueRow[] {
    if (tab === 'open') return bundle.data?.open ?? [];
    if (tab === 'waiting') return bundle.data?.waiting ?? [];
    if (tab === 'past_sla') {
      return (bundle.data?.open ?? []).filter(
        (r) => Date.now() - new Date(r.createdAt).getTime() > slaMs,
      );
    }
    return bundle.data?.recent ?? [];
  }
  const list = listForTab();

  // Mirror the tile-disabled rule for the chip: hide the chip + auto-fall
  // back to 'open' if the user lands on 'past_sla' with overdue=0.
  const overdueCount = stats?.overdue ?? 0;
  if (tab === 'past_sla' && overdueCount === 0 && bundle.data) {
    // Run a microtask so we don't setState in render.
    queueMicrotask(() => setTab('open'));
  }

  const q = search.trim().toLowerCase();
  const filtered = !q
    ? list
    : list.filter(
        (r) =>
          r.softwareName.toLowerCase().includes(q) ||
          (r.vendor ?? '').toLowerCase().includes(q) ||
          r.ticketNumber.toLowerCase().includes(q),
      );

  return (
    <>
      <div className="page-headbar reveal">
        <div>
          <div className="page-overline">{role === 'it' ? 'IT Reviewer' : 'Legal Reviewer'}</div>
          <h1 className="page-title">{role === 'it' ? 'IT Review Queue' : 'Legal Review Queue'}</h1>
          <p className="page-desc">
            {role === 'it'
              ? `Requests awaiting your IT security review. Oldest first so ${term} stays tight.`
              : `Requests that cleared IT and now need Legal review. ${term} clock: ${slaHours}h.`}
          </p>
        </div>
      </div>

      <div className="stats reveal reveal-1" style={{ marginBottom: 18 }}>
        <button
          type="button"
          className={`stat stat--accent ${tab === 'open' ? 'is-active' : ''}`}
          aria-pressed={tab === 'open'}
          onClick={() => setTab('open')}
        >
          <span className="stat__label">Open</span>
          <span className="stat__value">{stats?.open ?? '…'}</span>
          <span className="stat__delta">awaiting your review</span>
          <span className="stat__hint">{tab === 'open' ? 'Filtering ↓' : 'Click to filter'}</span>
        </button>
        <button
          type="button"
          className={`stat stat--warning ${tab === 'waiting' ? 'is-active' : ''}`}
          aria-pressed={tab === 'waiting'}
          onClick={() => setTab('waiting')}
        >
          <span className="stat__label">Waiting on Requester</span>
          <span className="stat__value">{stats?.waiting ?? '…'}</span>
          <span className="stat__delta">clarification requested</span>
          <span className="stat__hint">{tab === 'waiting' ? 'Filtering ↓' : 'Click to filter'}</span>
        </button>
        <button
          type="button"
          className={`stat stat--danger ${tab === 'past_sla' ? 'is-active' : ''}`}
          aria-pressed={tab === 'past_sla'}
          aria-disabled={overdueCount === 0}
          disabled={overdueCount === 0}
          onClick={() => overdueCount > 0 && setTab('past_sla')}
        >
          <span className="stat__label">Past {term} ({slaHours}h)</span>
          <span className="stat__value">{stats?.overdue ?? '…'}</span>
          <span className="stat__delta">need attention today</span>
          <span className="stat__hint">
            {overdueCount === 0
              ? 'Nothing to filter'
              : tab === 'past_sla'
              ? 'Filtering ↓'
              : 'Click to filter'}
          </span>
        </button>
        <div className="stat stat--success stat--readonly">
          <span className="stat__label">Median time-in-stage</span>
          <span className="stat__value">
            {stats?.medianDays !== null && stats?.medianDays !== undefined
              ? `${stats.medianDays}d`
              : '—'}
          </span>
          <span className="stat__delta">across last 30 days</span>
        </div>
      </div>

      <div className="toolbar reveal reveal-2" style={{ marginBottom: 18 }}>
        <div className="search" style={{ maxWidth: 360 }}>
          <input
            placeholder="Search tool, vendor, or ticket #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search queue"
          />
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          {(['open', 'waiting', 'past_sla', 'recent'] as const)
            .filter((t) => t !== 'past_sla' || overdueCount > 0)
            .map((t) => {
              let count = 0;
              if (t === 'open') count = bundle.data?.open.length ?? 0;
              else if (t === 'waiting') count = bundle.data?.waiting.length ?? 0;
              else if (t === 'past_sla') count = overdueCount;
              else count = bundle.data?.recent.length ?? 0;
              return (
                <button
                  key={t}
                  className={`chip ${tab === t ? 'is-active' : ''}`}
                  aria-pressed={tab === t}
                  onClick={() => setTab(t)}
                >
                  {TAB_LABEL[t]} <span className="chip__count">· {count}</span>
                </button>
              );
            })}
        </div>
      </div>

      {bundle.isLoading && <p className="muted">Loading…</p>}

      {bundle.data && filtered.length === 0 && (
        <div
          className="card card--padded reveal reveal-3"
          style={{ textAlign: 'center', padding: '64px 22px' }}
        >
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 6 }}>
            {EMPTY_HEADLINE[tab]}
          </div>
          {EMPTY_SUB[tab] && (
            <p className="muted" style={{ fontSize: 13, maxWidth: '46ch', margin: '0 auto' }}>
              {EMPTY_SUB[tab]}
            </p>
          )}
        </div>
      )}

      {bundle.data && filtered.length > 0 && (
        <div className="table-wrap reveal reveal-3">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Ticket</th>
                <th>Tool</th>
                <th style={{ width: 220 }}>Submitter</th>
                <th style={{ width: 130 }}>Risk</th>
                <th style={{ width: 180 }}>Status</th>
                <th style={{ width: 140 }}>Age</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const ageMs = Date.now() - new Date(r.createdAt).getTime();
                const overdue = (tab === 'open' || tab === 'past_sla') && ageMs > slaMs;
                const ageLabel = formatRelative(r.createdAt).replace(' ago', ' old');
                return (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/requests/${r.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="mono">{r.ticketNumber}</td>
                    <td>
                      <div className="tool-name">{r.softwareName}</div>
                      <div className="tool-sub">
                        {r.vendor ? `${r.vendor} · ${r.category}` : r.category}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={r.submitterName} />
                        <div>
                          <div style={{ fontWeight: 800, fontSize: 12 }}>{r.submitterName}</div>
                          <div style={{ fontSize: 10, color: 'var(--color-steel)' }}>
                            {r.submitterEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <RiskPill risk={r.risk} />
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: overdue ? 'var(--color-red)' : 'var(--color-steel)',
                        }}
                      >
                        {ageLabel}
                        {overdue && <span style={{ marginLeft: 6 }}>· {term}</span>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Avatar({ name }: Readonly<{ name: string }>) {
  const initials =
    name
      .replace(/[^A-Za-z ]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]!.toUpperCase())
      .join('') || '·';
  return (
    <span className="comment__avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
      {initials}
    </span>
  );
}
